import Guard from 'arg-guard'

import Service from './service'
import ServiceOperation from './service-operation'
import Logger from '@/logger'

/**
 * Gateway for accessing ReCaptcha lookups.
 *
 * @export CaptchaService
 * @class CaptchaService
 */
export default class CaptchaService extends Service {

    /**
     * Creates an instance of CaptchaService.
     * @memberof CaptchaService
     */
    constructor() {
        super('/captcha', 'Captcha')
    }

    /**
     * Bypases ReCaptcha by triggering the API to return a coookie as if ReCaptcha HAD been passed.
     *
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof CaptchaService
     */
    async bypassAsync() {
        Logger.logObject(this, 'Calling Bypass', 'ReCaptcha')
        const operationState = new ServiceOperation('Captcha Verification Bypass', true)
        const url = `bypass/803495780347ktw57tw385dts4u8tqlsq357tw023lutch443`
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [GET]')

        try {
            const response = await this.axios.get(url, this._buildAxiosConfig())
            if (response.data == null) {
                operationState.complete(false)
            } else {
                const output = { result: response.data }
                Logger.logObject(output, `Data returned [bypassAsync]`, this.loggingCategory)
                operationState.complete(true, output)
            }

            return operationState
        } catch (err) {
            return this._failOperation(err, operationState, 'bypassAsync')
        }
    }

    /**
     * Determines whether the Captcha has previously been verified.
     *
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof CaptchaService
     */
    async isVerifiedAsync() {
        const operationState = new ServiceOperation('Captcha Verification Check', true)
        const url = `isverified`
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [GET]')

        try {
            const response = await this.axios.get(url, this._buildAxiosConfig())
            if (response.data == null) {
                operationState.complete(false)
            } else {
                const output = { result: response.data }
                Logger.logObject(output, `Data returned [isVerifiedAsync]`, this.loggingCategory)
                operationState.complete(true, output)
            }

            return operationState
        } catch (err) {
            return this._failOperation(err, operationState, 'isVerifiedAsync')
        }
    }

    /**
     * Verifies the response form Captcha.
     *
     * @param {string} captchaResponse
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof CaptchaService
     */
    async verifyAsync(captchaResponse) {
        Guard.throwIf(captchaResponse, 'response')

        const operationState = new ServiceOperation('Captcha Verification', true)
        const url = `verify`
        const data = {
            input: captchaResponse,
        }

        Logger.logObject(data, `${ this.baseUrl }/${ url }`, 'Request [POST]')

        try {
            const response = await this.axios.post(url, data, this._buildAxiosConfig())
            Logger.logObject(response.data, 'Data returned [verifyAsync]', this.loggingCategory)
            operationState.complete(true, response.data)
            return operationState
        } catch (err) {
            return this._failOperation(err, operationState, 'verifyAsync')
        }
    }
}
